import {
  Button,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  IconButton,
  Modal,
  ModalContent,
  ModalOverlay,
  Input,
  ModalFooter,
  ModalBody,
  ModalHeader,
  useToast,
  Select,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import { FiPlus, FiPlusCircle } from "react-icons/fi";
import { AdminsTable } from "./AdminsTable";
import actions from "../../actions";
import { IKYDUser, KYDOrganizationUser } from "@common/interfaces/KYDUser";
import PhoneInput from "react-phone-input-2";
import parsePhoneNumber from "libphonenumber-js";
import { CognitoUser } from "amazon-cognito-identity-js";
import { useDialog, useDialogWithOptions } from "../../Common/Dialog";
import { KYDOrganizationFull } from "@common/interfaces/KYDOrganization";
import { FaPlus } from "react-icons/fa";
import { KYDScope } from "@common/interfaces/ICognito";
import CreateTeamMemberDialog, {
  CreateTeamMemberDialogContext,
  withCreateTeamMemberDialog,
} from "./CreateTeamMemberDialog";

enum EditOrganizationModalType {
  INACTIVE,
  ADD_ADMIN,
  DELETE_ADMIN_CONFIRM,
}

const App = ({
  currentUser,
  scopes,
}: {
  currentUser: KYDOrganizationUser | null;
  scopes: KYDScope[];
}) => {
  const [users, setUsers] = useState<KYDOrganizationUser[] | null>(null);
  const [loading, setLoading] = useState<any>({});
  const [modalType, setModalType] = useState(
    EditOrganizationModalType.INACTIVE
  );
  const [dirtyText, setDirtyText] = useState<any>({});
  const [editingTeamMember, setEditingTeamMember] =
    useState<KYDOrganizationUser | null>(null);
  const toast = useToast();
  const dialog = useDialog();

  const {
    open: openCreateTeamMemberDialog,
    close: closeCreateTeamMemberDialog,
  } = useDialogWithOptions(CreateTeamMemberDialogContext);

  const updateLoading = useCallback((key: string, value: boolean) => {
    setLoading((l: any) => {
      return { ...l, [key]: value };
    });
  }, []);

  const fetchOrganizationUsers = useCallback(
    async (silent?: boolean) => {
      if (!silent) {
        updateLoading("init", true);
      }
      let users = null;
      try {
        const response = await actions.ADMIN.fetchOrganizationUsers();
        setUsers(response.users);
        users = response.users;
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
      return users;
    },
    [updateLoading, dialog]
  );

  const onUpdateUser = useCallback(
    async (userId: string, scopes: KYDScope[]) => {
      updateLoading("organization", true);
      try {
        await actions.ADMIN.updateUserScopes(userId, scopes);
        await fetchOrganizationUsers(true);
        closeCreateTeamMemberDialog();
      } finally {
        updateLoading("organization", false);
      }
    },
    [updateLoading, fetchOrganizationUsers, closeCreateTeamMemberDialog]
  );

  const sleep = (milliseconds: number) => {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  };

  useEffect(() => {
    fetchOrganizationUsers();
  }, [updateLoading]);

  const onAddAdmin = useCallback(
    async (params: any) => {
      updateLoading("add_admin", true);
      try {
        const { first_name, last_name, email, phone_number, scopes } = params;

        const parsedPhoneNumber = parsePhoneNumber(`+${phone_number}`);
        if (!parsedPhoneNumber || !parsedPhoneNumber.number) {
          throw Error("Invalid phone number format");
        }

        const { user_id, did_exist } = await actions.ADMIN.createUser({
          first_name,
          last_name,
          email,
          phone_number: parsedPhoneNumber.number,
          scopes,
        });

        const added = () => {
          toast({
            title: "Admin Added",
            description: `"${first_name}" has been added`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          setModalType(EditOrganizationModalType.INACTIVE);
          closeCreateTeamMemberDialog();
        };

        if (!did_exist) {
          added();
          void (async () => {
            let found_admin = false;
            let attempts = 0;
            do {
              const users = await fetchOrganizationUsers(true);
              if (users) {
                found_admin = !!users.find((a) => a.id === user_id);
              }
              attempts += 1;
              await sleep(3000);
            } while (!found_admin && attempts < 5);
          })();
        } else {
          await fetchOrganizationUsers(true);
          added();
        }
      } finally {
        updateLoading("add_admin", false);
      }
    },
    [fetchOrganizationUsers, closeCreateTeamMemberDialog, toast]
  );

  const onRemoveAdmin = useCallback(
    async (user: KYDOrganizationUser) => {
      updateLoading("remove_admin", true);
      updateLoading(user.id, true);
      try {
        await actions.ADMIN.deleteUser(user.id);
        await fetchOrganizationUsers(true);
        toast({
          title: "Admin Removed",
          description: `Admin has been removed`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        closeCreateTeamMemberDialog();
      } finally {
        updateLoading("remove_admin", false);
        updateLoading(user.id, false);
      }
    },
    [fetchOrganizationUsers, closeCreateTeamMemberDialog, toast]
  );

  return (
    <Stack maxW={"5xl"} mx="auto" spacing={{ base: "8", lg: "6" }}>
      <CreateTeamMemberDialog
        onDelete={onRemoveAdmin}
        onClose={() => {
          setEditingTeamMember(null);
        }}
        onInvite={onAddAdmin}
        onUpdate={onUpdateUser}
        editingTeamMember={editingTeamMember}
        isLoading={Boolean(
          loading.add_admin || loading.organization || loading.remove_admin
        )}
      />
      <Stack
        spacing="4"
        direction={{ base: "column", lg: "row" }}
        justify="space-between"
        align={{ base: "start", lg: "center" }}
      >
        <Stack spacing="1" w="100%">
          <Heading
            size={useBreakpointValue({ base: "xs", lg: "sm" })}
            fontWeight="medium"
          >
            <HStack justify={"space-between"}>
              <Text fontSize={"lg"} fontWeight="bold">
                Team
              </Text>
              {currentUser?.scopes?.includes("organizations/write") ||
              currentUser?.scopes?.includes("organizations/users:write") ? (
                <IconButton
                  isDisabled={loading["organization"]}
                  variant={"outline"}
                  aria-label="Add user"
                  icon={<FaPlus />}
                  onClick={() => {
                    openCreateTeamMemberDialog();
                  }}
                />
              ) : null}
            </HStack>
          </Heading>
        </Stack>
      </Stack>

      {loading["init"] && (
        <VStack>
          <Spinner />
          <Text>Loading team...</Text>
        </VStack>
      )}
      {users && (
        <Stack overflowX={"scroll"} spacing={{ base: "5", lg: "6" }}>
          <AdminsTable
            currentUser={currentUser}
            loading={loading}
            onEditAdmin={(admin) => {
              setEditingTeamMember(admin);
              openCreateTeamMemberDialog();
            }}
            users={users}
            scopes={scopes}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default withCreateTeamMemberDialog(App);
